/* eslint-disable no-console */
// The one module allowed to touch console - everything else goes through here.
const LEVELS = { debug: 10, info: 20, warn: 30, error: 40, silent: 99 };

/**
 * Resolve the active log level.
 *
 * Reads localStorage first so a specific device can be turned verbose in
 * production without a rebuild - this app has no error tracking, so that
 * override is the only way to get diagnostics off a user's machine.
 *
 * @returns {number} Numeric threshold; messages below it are dropped.
 */
const resolveThreshold = () => {
    let stored = null;
    try {
        stored = typeof localStorage !== 'undefined' ? localStorage.getItem('logLevel') : null;
    } catch (e) {
        // Safari in private mode throws on localStorage access.
    }

    const configured =
        stored || process.env.REACT_APP_LOG_LEVEL || (process.env.NODE_ENV === 'production' ? 'warn' : 'debug');

    return LEVELS[configured] ?? LEVELS.debug;
};

const threshold = resolveThreshold();

const emit =
    (level, method) =>
    (...args) => {
        if (LEVELS[level] >= threshold) console[method](...args);
    };

/**
 * Application logger.
 *
 * debug/info are development-only by default; warn/error always print, so
 * production keeps its error signal. `log` is an alias for `debug` kept for
 * the call sites that were already written against a `const logger = console`
 * stub.
 */
export const logger = {
    debug: emit('debug', 'log'),
    log: emit('debug', 'log'),
    info: emit('info', 'info'),
    warn: emit('warn', 'warn'),
    error: emit('error', 'error'),
};

export default logger;
